import './wdyr';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';
import { Suspense, lazy } from 'react';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
import { Provider } from 'react-redux';
import { store } from './store';

// Critical components - load immediately
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';

// Lazy load Layout and Dashboard components to prevent loading on login page
const Layout = lazy(() => import('./components/Layout'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const DashboardRedirect = lazy(() => import('./components/DashboardRedirect'));
const AuthenticatedProviders = lazy(() => import('./components/AuthenticatedProviders'));

// Lazy load ALL components to prevent loading on login page
const Center = lazy(() => import('./components/Center'));
const Class = lazy(() => import('./components/Class'));
const ClassDetail = lazy(() => import('./components/ClassDetail'));
const Branch = lazy(() => import('./components/Branch'));
const Department = lazy(() => import('./components/Department'));
const DepartmentDetail = lazy(() => import('./components/DepartmentDetail'));
const Teacher = lazy(() => import('./feutures/teachers/screen/Teacher'));
const TeacherDetail = lazy(() => import('./feutures/teachers/screen/TeacherDetail'));
const TeacherDashboard = lazy(() => import('./feutures/teachers/components/Dashboard'));
const OwnerDashboard = lazy(() => import('./components/OwnerDashboard'));
const BranchManagerDashboard = lazy(() => import('./components/BranchManagerDashboard'));
const StudentComponent = lazy(() => import('./feutures/students/screen/Student'));
const StudentDetail = lazy(() => import('./feutures/students/screen/StudentDetail'));
const StudentDashboard = lazy(() => import('./feutures/students/components/StudentDashboard'));
const Attendance = lazy(() => import('./feutures/attendance/screen/Attendance'));
const Fees = lazy(() => import('./components/Fees'));
const FeesDashboard = lazy(() => import('./components/FeesDashboard'));
const Enrollment = lazy(() => import('./components/Enrollment'));
const Course = lazy(() => import('./components/Course'));
const CourseDetail = lazy(() => import('./components/CourseDetail'));

// Lazy load exam components
const ExamDashboard = lazy(() => import('./feutures/exam/components/ExamDashboard'));
const ExamGrades = lazy(() => import('./feutures/exam/components/ExamGrades'));

const Staff = lazy(() => import('./components/Staff'));
const StaffDetail = lazy(() => import('./components/StaffDetail'));
const StaffDashboard = lazy(() => import('./feutures/staff/components/StaffDashboard'));
const StaffPayroll = lazy(() => import('./feutures/staff/components/Payroll'));
const StaffAttendance = lazy(() => import('./feutures/staff/components/StaffAttendance'));
const StaffAttendanceSettings = lazy(() => import('./feutures/staff/StaffAttendanceSettings'));
const NotificationManager = lazy(() => import('./components/NotificationManager'));
const Expenses = lazy(() => import('./feutures/finance/components/Expenses'));
const Donations = lazy(() => import('./feutures/finance/components/Donations'));
const FinanceDashboard = lazy(() => import('./feutures/finance/components/FinanceDashboard'));
const IncomeScreen = lazy(() => import('./feutures/finance/components/IncomeScreen'));
const FinanceScreen = lazy(() => import('./feutures/finance/FinanceScreen'));
const ReportsComponent = lazy(() => import('./feutures/reports/screen/Reports'));

// RTL-aware Toaster component
const RTLToaster = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'fa' || i18n.language === 'ps';
  
  return (
    <Toaster
      position="top-right"
      containerStyle={{
        zIndex: 99999,
      }}
      toastOptions={{
        duration: 4000,
        style: {
          background: '#ffffff',
          color: '#1f2937',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left',
          minWidth: '300px',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 99999,
        },
        success: {
          duration: 4000,
          style: {
            background: '#ffffff',
            color: '#065f46',
            borderLeft: '4px solid #10b981',
          },
          iconTheme: {
            primary: '#10b981',
            secondary: '#fff',
          },
        },
        error: {
          duration: 4000,
          style: {
            background: '#ffffff',
            color: '#991b1b',
            borderLeft: '4px solid #ef4444',
          },
          iconTheme: {
            primary: '#ef4444',
            secondary: '#fff',
          },
        },
      }}
    >
      {(t) => (
        <div style={{ 
          position: 'relative', 
          width: '300px', 
          maxWidth: '300px',
          background: '#ffffff',
          borderRadius: '8px',
          padding: '16px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          borderLeft: t.type === 'success' ? '4px solid #10b981' : '4px solid #ef4444',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ 
              fontSize: '20px', 
              flexShrink: 0,
              color: t.type === 'success' ? '#10b981' : '#ef4444',
            }}>
              {t.type === 'success' ? '✓' : '✕'}
            </span>
            <span style={{ 
              flex: 1, 
              wordBreak: 'break-word',
              color: t.type === 'success' ? '#065f46' : '#991b1b',
            }}>
              {t.message?.toString()}
            </span>
          </div>
          <div 
            className={`toast-progress-bar ${t.type}`}
            style={{ 
              position: 'absolute',
              bottom: '0',
              left: '0',
              right: '0',
              width: '100%',
              borderBottomLeftRadius: '8px',
              borderBottomRightRadius: '8px',
            }}
          />
        </div>
      )}
    </Toaster>
  );
};

function App() {
  return (
    <Provider store={store}>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
              <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <RTLToaster />
                <Routes>
                  {/* Public Routes - AuthProvider available, DarkMode via Redux */}
                  <Route path="/login" element={
                    <ProtectedRoute requireAuth={false}>
                      <LoginPage />
                    </ProtectedRoute>
                  } />

                  {/* Protected Routes with additional lazy-loaded providers */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                      <AuthenticatedProviders>
                        <Layout />
                      </AuthenticatedProviders>
                    </Suspense>
                  </ProtectedRoute>
                }>
                      <Route index element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <DashboardRedirect />
                        </Suspense>
                      } />
                      <Route path="centers" element={
                        <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                            <Center />
                          </Suspense>
                        </ProtectedRoute>
                      } />
                      <Route path="classes" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <Class />
                        </Suspense>
                      } />
                      <Route path="classes/:id" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <ClassDetail />
                        </Suspense>
                      } />
                      <Route path="branches" element={
                        <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                            <Branch />
                          </Suspense>
                        </ProtectedRoute>
                      } />
                      <Route path="departments" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <Department />
                        </Suspense>
                      } />
                      <Route path="departments/:id" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <DepartmentDetail />
                        </Suspense>
                      } />
                      <Route path="teachers" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <Teacher />
                        </Suspense>
                      } />
                      <Route path="teachers/:id" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <TeacherDetail />
                        </Suspense>
                      } />
                      <Route path="teachers/dashboard" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <TeacherDashboard />
                        </Suspense>
                      } />
                      <Route path="staff" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <Staff />
                        </Suspense>
                      } />
                      <Route path="staff/:id" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <StaffDetail />
                        </Suspense>
                      } />
                      <Route path="staff/dashboard" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <StaffDashboard />
                        </Suspense>
                      } />
                      <Route path="staff/payroll" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <StaffPayroll />
                        </Suspense>
                      } />
                      <Route path="staff/attendance" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <StaffAttendance />
                        </Suspense>
                      } />
                      <Route path="staff/attendance-settings" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <StaffAttendanceSettings />
                        </Suspense>
                      } />
                      <Route path="students" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <StudentComponent />
                        </Suspense>
                      } />
                      <Route path="students/:id" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <StudentDetail />
                        </Suspense>
                      } />
                      <Route path="students/dashboard" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <StudentDashboard />
                        </Suspense>
                      } />
                      <Route path="courses" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <Course />
                        </Suspense>
                      } />
                      <Route path="courses/:id" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <CourseDetail />
                        </Suspense>
                      } />
                      <Route path="attendance" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <Attendance />
                        </Suspense>
                      } />
                      <Route path="enrollments" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <Enrollment />
                        </Suspense>
                      } />
        
                      <Route path="fees" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <Fees />
                        </Suspense>
                      } />
                      <Route path="fees/dashboard" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <FeesDashboard />
                        </Suspense>
                      } />
                      <Route path="finance/*" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <FinanceScreen />
                        </Suspense>
                      } />
                      <Route path="finance/dashboard" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <FinanceDashboard />
                        </Suspense>
                      } />
                      <Route path="finance/expenses" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <Expenses />
                        </Suspense>
                      } />
                      <Route path="finance/donations" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <Donations />
                        </Suspense>
                      } />
                      <Route path="finance/income" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <IncomeScreen />
                        </Suspense>
                      } />
                      <Route path="finance/payroll" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <StaffPayroll />
                        </Suspense>
                      } />
                      <Route path="reports" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <ReportsComponent />
                        </Suspense>
                      } />
                      <Route path="notifications" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <NotificationManager />
                        </Suspense>
                      } />
                      <Route path="dashboard" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <DashboardPage />
                        </Suspense>
                      } />
                      <Route path="owner-dashboard" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <OwnerDashboard />
                        </Suspense>
                      } />
                      <Route path="branch-manager-dashboard" element={
                        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <BranchManagerDashboard />
                        </Suspense>
                      } />
                      
                      {/* Exam Routes */}
                      <Route path="exams" element={
                        <Suspense fallback={
                          <div className="min-h-screen flex items-center justify-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                          </div>
                        }>
                          <ExamDashboard />
                        </Suspense>
                      } />
                      <Route path="exams/grades" element={
                        <Suspense fallback={
                          <div className="min-h-screen flex items-center justify-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                          </div>
                        }>
                          <ExamGrades />
                        </Suspense>
                      } />
                      
                      {/* Redirect old routes to prevent errors */}
                      <Route path="activities" element={<Navigate to="/dashboard" replace />} />
                      
                      {/* Catch-all route for undefined paths */}
                      <Route path="*" element={
                        <div className="min-h-screen flex items-center justify-center bg-gray-50">
                          <div className="text-center">
                            <h1 className="text-2xl font-bold text-gray-900 mb-4">Page Not Found</h1>
                            <p className="text-gray-600 mb-4">The page you're looking for doesn't exist.</p>
                            <a href="/" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                              Go to Dashboard
                            </a>
                          </div>
                        </div>
                      } />
                    </Route>
                  </Routes>
                </div>
              </Router>
          </AuthProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </ErrorBoundary>
    </Provider>
  );
}

export default App;
